/* card-swipe.js — Sneak Freak (AphCom)
   The swiping itself needs no JavaScript: .card-b__track is a native scroll container with scroll-snap, so
   the browser already distinguishes a tap from a drag and keeps the gesture inside the card. This file does
   one thing only — the first-card hint.

   Once per collection-page entry, on mobile, the first product card glides most of the way to its second
   image, holds there long enough for that image to register, and eases back to the first. It is meant to
   read as a demonstration, not a twitch.

   The second image is fetched and DECODED before any movement starts, so the glide never runs against a
   blank or half-painted slide. Exactly one extra image is loaded; every other slide stays lazy. If that
   image does not arrive promptly the hint is skipped rather than shown badly.

   It yields the moment the shopper touches the card, scrolls, presses a key, or picks a colourway — control
   passes straight to them and scroll-snap settles wherever they left it. It runs once: the flag lives on
   window, so filtering, sorting, scrolling and Section Rendering updates leave it alone, while a real
   navigation starts a fresh page and a fresh flag. Nothing happens at all under prefers-reduced-motion or
   on desktop. */
'use strict';
(function () {
  if (window.__sfCardHint) return;
  window.__sfCardHint = { done: false };

  const DESKTOP = 1024;
  const REVEAL = 0.58;        /* how far toward the second image, as a share of the card's width */
  const OUT_MS = 620;         /* glide out */
  const HOLD_MS = 520;        /* pause with the second image showing */
  const BACK_MS = 680;        /* ease back to the first */
  const DECODE_BUDGET_MS = 2500;

  const easeOut = (t) => 1 - Math.pow(1 - t, 3);
  const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  function reduced() {
    if (window.theme && window.theme.reducedMotion) return true;
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function firstTrack() {
    const grid = document.querySelector('[data-facets-grid], .plp-grid');
    return grid ? grid.querySelector('[data-card-track]') : null;
  }

  /* Fetch and decode ONLY the second slide's image. It is already in the DOM as a lazy image; promoting
     that one element is what keeps this from touching any other card. */
  function readySecondImage(track) {
    const slides = track.querySelectorAll('.card-b__slide');
    if (slides.length < 2) return Promise.reject(new Error('one image'));
    const img = slides[1].querySelector('img');
    if (!img) return Promise.reject(new Error('no image'));
    if (img.complete && img.naturalWidth > 0) return Promise.resolve(img);

    img.loading = 'eager';
    if ('fetchPriority' in img) img.fetchPriority = 'high';

    const decoded = typeof img.decode === 'function'
      ? img.decode()
      : new Promise((resolve, reject) => {
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', reject, { once: true });
      });

    const budget = new Promise((_, reject) => setTimeout(() => reject(new Error('slow')), DECODE_BUDGET_MS));
    return Promise.race([decoded, budget]).then(() => {
      if (!(img.complete && img.naturalWidth > 0)) throw new Error('not painted');
      return img;
    });
  }

  /* Right-to-left (Arabic): the slides run leftward, and a direction: rtl scroller reports scrollLeft as 0 at
     its start and NEGATIVE toward its end. The hint wrote positive values, which Chrome clamps to 0, so in
     RTL it ran its whole 1.8s without moving and still fetched the second image; and the "already moved"
     guards, testing scrollLeft > 2, could never see a track the shopper had swiped. */
  const rtlSign = (track) => (getComputedStyle(track).direction === 'rtl' ? -1 : 1);
  const moved = (track) => Math.abs(track.scrollLeft) > 2;

  function glide(track) {
    const distance = Math.round(track.clientWidth * REVEAL);
    if (distance < 24) return;
    const sign = rtlSign(track);

    track.classList.add('is-hinting');
    track.setAttribute('data-hint-state', 'running');

    let raf = 0;
    let cancelled = false;

    const release = (settleHome) => {
      if (cancelled) return;
      cancelled = true;
      cancelAnimationFrame(raf);
      teardown();
      if (settleHome) track.scrollLeft = 0;
      track.classList.remove('is-hinting');
      track.setAttribute('data-hint-state', settleHome ? 'done' : 'yielded');
    };

    /* Any sign of the shopper taking part ends it immediately. We never listen to the track's own scroll
       event — we are the ones writing scrollLeft, so that would cancel us on our own first frame.
       The touch listeners sit on the whole CARD, not just the track: a finger on the colour strip under the
       photo is also the shopper taking over, and a strip dragged while this loop kept gliding the photo
       above it read as one drag moving both scrollers (measured: the photo slid 97px back to its first image
       during a strip drag). A finger on the photo takes the track over where it is; one anywhere else on the
       card sends the photo home in one short glide, because letting scroll-snap settle from mid-hint jumped
       it on to the SECOND image under a finger that was on the strip. */
    const yieldNow = () => release(false);
    const yieldCard = (e) => {
      const onTrack = !!(e && e.target && track.contains(e.target));
      release(false);
      if (!onTrack) track.scrollTo({ left: 0, behavior: reduced() ? 'auto' : 'smooth' });
    };
    const opts = { passive: true };
    const host = track.closest('.card-b') || track;
    const teardown = () => {
      host.removeEventListener('pointerdown', yieldCard, opts);
      host.removeEventListener('touchstart', yieldCard, opts);
      host.removeEventListener('wheel', yieldCard, opts);
      window.removeEventListener('scroll', yieldNow, opts);
      window.removeEventListener('keydown', yieldNow, opts);
      window.removeEventListener('colors:change', yieldNow, opts);
    };
    host.addEventListener('pointerdown', yieldCard, opts);
    host.addEventListener('touchstart', yieldCard, opts);
    host.addEventListener('wheel', yieldCard, opts);
    window.addEventListener('scroll', yieldNow, opts);
    window.addEventListener('keydown', yieldNow, opts);
    /* a colour picked from the keyboard involves no pointer at all — and card-colors.js writing scrollLeft
       to 0 would fight this rAF loop for the rest of its run */
    window.addEventListener('colors:change', yieldNow, opts);

    const started = performance.now();
    const total = OUT_MS + HOLD_MS + BACK_MS;

    const step = (now) => {
      if (cancelled) return;
      const t = now - started;
      let x;
      if (t < OUT_MS) x = distance * easeOut(t / OUT_MS);
      else if (t < OUT_MS + HOLD_MS) x = distance;
      else if (t < total) x = distance * (1 - easeInOut((t - OUT_MS - HOLD_MS) / BACK_MS));
      else { release(true); return; }
      track.scrollLeft = sign * x;
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
  }

  function attempt() {
    const state = window.__sfCardHint;
    if (state.done) return true;
    if (window.innerWidth >= DESKTOP || reduced()) { state.done = true; return true; }
    if (document.documentElement.classList.contains('sf-sheet-open')) return false;

    const track = firstTrack();
    if (!track) return false;
    if (track.clientWidth < 40 || track.scrollWidth <= track.clientWidth + 4) return false;
    /* only worth doing if it will actually be seen */
    const box = track.getBoundingClientRect();
    if (box.bottom < 0 || box.top > window.innerHeight) return false;
    /* and never on a card the shopper has already moved (either direction: see rtlSign) */
    if (moved(track)) { state.done = true; return true; }

    state.done = true;
    readySecondImage(track)
      .then(() => {
        /* conditions can change while the image is in flight */
        if (window.innerWidth >= DESKTOP || reduced()) return;
        if (document.documentElement.classList.contains('sf-sheet-open')) return;
        if (moved(track) || !track.isConnected) return;
        const b = track.getBoundingClientRect();
        if (b.bottom < 0 || b.top > window.innerHeight) return;
        glide(track);
      })
      .catch(() => {
        /* slow or broken image: no hint rather than a broken-looking one */
        track.setAttribute('data-hint-state', 'skipped');
      });
    return true;
  }

  function arm() {
    if (attempt()) return;
    let tries = 0;
    const timer = setInterval(() => {
      if (attempt() || ++tries > 12) clearInterval(timer);
    }, 250);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', arm, { once: true });
  else arm();
})();
